import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Upload, UserPlus } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABELS,
  WALK_IN_SOURCE,
  cvMaxBytes,
  cvProblem,
  customValueProblem,
  manualCandidateSchema,
  megabytes,
  type CustomFieldValue,
  type ManualCandidateInput,
  type ManualCandidateResult,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { MoreOptions } from '../../../components/MoreOptions';
import { SaveBar } from '../../../components/SaveBar';
import { Checkbox } from '../../../components/Checkbox';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { SettingRow } from '../../../components/SettingRow';
import { Select } from '../../../components/Select';
import { ForbiddenState } from '../../../components/system-states/SystemState';
import { errorMessage, request } from '../../../lib/api-client';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useSourceOptions } from '../../../lib/app-settings';
import { useModule } from '../../../lib/modules';
import { useToasts } from '../../../stores/toasts';
import { useJobs } from '../../jobs/api/jobs-api';
import { usePoolOptions } from '../../pools/api/pools-api';
import { useCreateCandidate } from '../api/candidates-api';
import { useCustomFields, useSaveCustomValues } from '../../admin/api/settings-api';
import { CustomFieldControl, wireValue } from '../../admin/components/CustomFieldControl';
import { addCandidateInSteps, partialAddMessage } from '../add-candidate-format';
import styles from './AddCandidatePage.module.css';

/**
 * CAND-07 / F7.10 — a walk-in, a paper CV, someone met at a fair. Recognised through
 * the same §L2 path as the career site. A job is optional (E5; S14 Q2/Q5); the CV
 * too, because a paper CV is not always scanned.
 */

const OPEN_JOBS_PAGE = 200;
const NO_JOB = '';
const RELOCATE_OPTIONS = [
  { value: 'unknown', label: 'Not asked' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;
type Relocate = (typeof RELOCATE_OPTIONS)[number]['value'];

/** What the inputs hold — text — before it becomes the shared request. */
const formSchema = z
  .object({
    fullName: z.string(),
    email: z.string(),
    phone: z.string(),
    city: z.string(),
    currentEmployer: z.string(),
    currentTitle: z.string(),
    totalExperienceYears: z.string(),
    highestEducation: z.string(),
    skills: z.string(),
    certifications: z.string(),
    expectedSalary: z.string(),
    noticePeriodDays: z.string(),
    isWillingToRelocate: z.enum(['unknown', 'yes', 'no']),
    nationalId: z.string(),
    jobId: z.string(),
    source: z.string(),
    poolId: z.string(),
    hasConsent: z.boolean(),
  })
  .transform(toRequest)
  .pipe(manualCandidateSchema);

type FormValues = z.input<typeof formSchema>;

const EMPTY_FORM: FormValues = {
  fullName: '',
  email: '',
  phone: '',
  city: '',
  currentEmployer: '',
  currentTitle: '',
  totalExperienceYears: '',
  highestEducation: '',
  skills: '',
  certifications: '',
  expectedSalary: '',
  noticePeriodDays: '',
  isWillingToRelocate: 'unknown',
  nationalId: '',
  jobId: NO_JOB,
  source: WALK_IN_SOURCE,
  poolId: '',
  hasConsent: false,
};

export function AddCandidatePage(): ReactNode {
  const sourceOptions = useSourceOptions('record');
  const canAdd = useCanEdit('candidate');
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const create = useCreateCandidate();
  const jobs = useJobs({ page: 1, pageSize: OPEN_JOBS_PAGE, status: 'open' });
  const isPoolsOn = useModule('pools');
  const canSeePools = useCanView('talent_pool') && isPoolsOn;
  const pools = usePoolOptions(canSeePools);
  const [cv, setCv] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // R1 (§L34): with Custom fields off, the form neither shows nor checks them.
  const isCustomFieldsOn = useModule('custom_fields');
  const customFields = useCustomFields('candidate');
  const activeCustomFields = isCustomFieldsOn ? (customFields.data ?? []) : [];
  const saveCustom = useSaveCustomValues('candidate');
  const [customValues, setCustomValues] = useState<Readonly<Record<string, CustomFieldValue | null>>>({});
  const [customErrors, setCustomErrors] = useState<Readonly<Record<string, string>>>({});
  const form = useForm<FormValues, unknown, ManualCandidateInput>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
    mode: 'onBlur',
  });
  const errors = form.formState.errors;
  const jobId = useWatch({ control: form.control, name: 'jobId' });

  if (!canAdd) return <ForbiddenState resource="adding candidates" contact="Your admin" />;

  function chooseCv(file: File | undefined): void {
    if (!file) return;
    const problem = cvProblem({ fileName: file.name, byteSize: file.size });
    setCvError(problem);
    setCv(problem ? null : file);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    const problems = Object.fromEntries(activeCustomFields.flatMap((field) => {
      const problem = customValueProblem(field, customValues[field.id] ?? null);
      return problem ? [[field.id, problem]] : [];
    }));
    setCustomErrors(problems);
    if (Object.keys(problems).length > 0) return;
    const given = isCustomFieldsOn ? Object.entries(customValues).filter(([, value]) => value !== null) : [];
    let added: Awaited<ReturnType<typeof addCandidateInSteps<ManualCandidateResult>>>;
    try {
      added = await addCandidateInSteps<ManualCandidateResult>(
        () => create.mutateAsync(values),
        cv ? (result) => uploadCv(result.candidateId, result.applicationId, cv) : null,
        given.length > 0 ? (result) => saveCustom.mutateAsync({ entityId: result.candidateId, input: { values: Object.fromEntries(given.map(([id, value]) => [id, wireValue(value)])) } }) : null,
      );
    } catch (cause) {
      show({ tone: 'danger', message: 'The candidate was not added', detail: errorMessage(cause) });
      return;
    }
    const { result, failures, problems: stepProblems } = added;
    const openProfile = (): void => void navigate({ to: '/candidates/$candidateId', params: { candidateId: result.candidateId } });
    const partial = partialAddMessage(values.fullName, failures);
    if (partial) {
      // The person exists: say so truthfully, never "was not added" (Group 8).
      show({ tone: 'warning', message: partial, detail: errorMessage(stepProblems[0]), action: { label: 'Open their profile', run: openProfile } });
    } else {
      show({ tone: 'success', ...outcomeToast(result.outcome, values.fullName) });
    }
    if (result.applicationId) {
      void navigate({ to: '/applications/$applicationId', params: { applicationId: result.applicationId } });
    } else {
      openProfile();
    }
  });

  /** One labelled row per field (Add-candidate mockup, `.set`). */
  const row = (name: keyof FormValues, label: string, options: { hint?: string; isRequired?: boolean; inputMode?: 'numeric' | 'decimal' | 'email' | 'tel'; isAutoFocus?: boolean } = {}): ReactNode => {
    const error = errors[name]?.message;
    return (
      <SettingRow label={label} {...(options.hint ? { hint: options.hint } : {})} isRequired={options.isRequired ?? false} isOptional={!options.isRequired} {...(error ? { error } : {})}>
        {({ inputId, describedBy, isInvalid }) => (
          <Input
            id={inputId}
            aria-describedby={describedBy}
            isInvalid={isInvalid}
            autoComplete="off"
            autoFocus={options.isAutoFocus ?? false}
            {...(options.inputMode ? { inputMode: options.inputMode } : {})}
            {...form.register(name)}
          />
        )}
      </SettingRow>
    );
  };

  return (
    <form
      className={styles.page}
      noValidate
      onSubmit={(event) => void onSubmit(event)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void onSubmit();
        }
      }}
    >
      <header className={styles.header}>
        <Link to="/candidates" className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          Candidates
        </Link>
        <h1 className={styles.title}>Add a candidate</h1>
        <p className={styles.meta}>For a walk-in or a paper CV. If this person is already in the system, the details join their record instead.</p>
      </header>

      <Card title="Who they are" isFlush>
        <div className={styles.rows}>
          {row('fullName', 'Full name', { isRequired: true, isAutoFocus: true })}
          {row('phone', 'Mobile number', { hint: 'Any local format, for example 01712345678.', inputMode: 'tel' })}
          {row('email', 'Email', { hint: 'Give a mobile number or an email — it is how we recognise this person next time.', inputMode: 'email' })}
          {row('city', 'City')}
        </div>
      </Card>

      <Card title="Their work" isFlush>
        <div className={styles.rows}>
          {row('currentTitle', 'Current job title')}
          {row('currentEmployer', 'Current employer')}
          {row('totalExperienceYears', 'Years of experience', { inputMode: 'decimal', hint: 'For example 3 or 2.5.' })}
          <Controller
            control={form.control}
            name="highestEducation"
            render={({ field }) => (
              <SettingRow label="Highest education" isOptional {...(errors.highestEducation?.message ? { error: errors.highestEducation.message } : {})}>
                {({ inputId, describedBy, isInvalid }) => (
                  <Select
                    id={inputId}
                    describedBy={describedBy}
                    isInvalid={isInvalid}
                    label="Highest education"
                    placeholder="Not given"
                    value={field.value === '' ? null : field.value}
                    options={EDUCATION_LEVELS.map((level) => ({ value: level, label: EDUCATION_LEVEL_LABELS[level] }))}
                    onChange={field.onChange}
                  />
                )}
              </SettingRow>
            )}
          />
          {row('skills', 'Skills', { hint: 'Separate them with commas. These are what the match score reads.' })}
          {/* QoL §7 rule 4 (R9): what most walk-ins never give, closed by default. */}
          <div className={styles.more}>
            <MoreOptions label="Salary, notice, NID and more">
              <div className={styles.rows}>
                {row('expectedSalary', 'Expected monthly salary (gross), in taka', { inputMode: 'numeric' })}
                {row('noticePeriodDays', 'Notice period, in days', { inputMode: 'numeric' })}
                {row('nationalId', 'NID', { inputMode: 'numeric', hint: '10, 13 or 17 digits.' })}
                {row('certifications', 'Professional certifications', { hint: 'Separate them with commas.' })}
                <Controller
                  control={form.control}
                  name="isWillingToRelocate"
                  render={({ field }) => (
                    <SettingRow label="Willing to relocate" isGroup>
                      {() => <Segmented<Relocate> label="Willing to relocate" value={field.value} options={RELOCATE_OPTIONS} onChange={field.onChange} />}
                    </SettingRow>
                  )}
                />
              </div>
            </MoreOptions>
          </div>
        </div>
      </Card>

      <Card title="A job" meta={isPoolsOn ? 'Optional. Without one, the person is saved into a talent pool' : 'Optional. Without one, the person is saved on file'} isFlush>
        <div className={styles.rows}>
          <Controller
            control={form.control}
            name="jobId"
            render={({ field }) => (
              <SettingRow label="Job" isOptional {...(errors.jobId?.message ? { error: errors.jobId.message } : {})}>
                {({ inputId, describedBy }) => (
                  <Select
                    id={inputId}
                    describedBy={describedBy}
                    label="Job"
                    placeholder="No job — save the person only"
                    value={field.value === NO_JOB ? null : field.value}
                    options={[
                      { value: NO_JOB, label: 'No job — save the person only' },
                      ...(jobs.data?.rows ?? []).map((job) => ({ value: job.id, label: job.publicTitle ?? job.title, description: `${job.reference} · ${job.ventureName}` })),
                    ]}
                    onChange={field.onChange}
                  />
                )}
              </SettingRow>
            )}
          />
          {jobId === NO_JOB && isPoolsOn && (
            <Controller
              control={form.control}
              name="poolId"
              render={({ field }) => (
                <SettingRow label="Talent pool" hint="With no job, they go into a pool so they stay on file. General CVs unless you choose another.">
                  {({ inputId, describedBy }) => (
                    <Select
                      id={inputId}
                      describedBy={describedBy}
                      label="Talent pool"
                      placeholder="General CVs"
                      value={field.value === '' ? null : field.value}
                      options={(pools.data ?? []).map((pool) => ({ value: pool.id, label: pool.name }))}
                      onChange={field.onChange}
                    />
                  )}
                </SettingRow>
              )}
            />
          )}
          {jobId !== NO_JOB && (
            <Controller
              control={form.control}
              name="source"
              render={({ field }) => (
                <SettingRow label="Where they came from" {...(errors.source?.message ? { error: errors.source.message } : {})}>
                  {({ inputId, describedBy }) => <Select id={inputId} describedBy={describedBy} label="Where they came from" value={field.value} options={sourceOptions} onChange={field.onChange} />}
                </SettingRow>
              )}
            />
          )}
        </div>
      </Card>

      {activeCustomFields.length > 0 && (
        <Card title="More details">
          <div className={styles.fields}>
            {activeCustomFields.map((field) => (
              <CustomFieldControl
                key={field.id}
                field={field}
                value={customValues[field.id] ?? null}
                error={customErrors[field.id]}
                onChange={(value) => setCustomValues({ ...customValues, [field.id]: value })}
              />
            ))}
          </div>
        </Card>
      )}

      <Card title="CV" meta={`Optional. PDF, DOC or DOCX, up to ${megabytes(cvMaxBytes())} MB`}>
        <div className={styles.cv}>
          <input
            ref={fileInput}
            className={styles.hiddenFile}
            type="file"
            accept=".pdf,.doc,.docx"
            aria-label="Choose a CV file"
            onChange={(event) => {
              chooseCv(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <Button variant="outline" icon={Upload} onClick={() => fileInput.current?.click()}>
            {cv ? 'Choose a different file' : 'Choose a CV'}
          </Button>
          {cv && <span className={styles.fileName}>{cv.name}</span>}
          {cvError && (
            <span className={styles.error} role="alert">
              {cvError}
            </span>
          )}
        </div>
      </Card>

      <SaveBar
        status={
          <Controller
            control={form.control}
            name="hasConsent"
            render={({ field }) => (
              <div className={styles.consent}>
                <Checkbox checked={field.value} onChange={(checked) => field.onChange(checked)} label="They agreed to us keeping their details and CV for this and similar roles" />
                {errors.hasConsent && (
                  <span className={styles.error} role="alert">
                    {errors.hasConsent.message}
                  </span>
                )}
              </div>
            )}
          />
        }
      >
        <Button variant="solid" type="submit" icon={UserPlus} isPending={form.formState.isSubmitting}>
          Add candidate
        </Button>
      </SaveBar>
    </form>
  );
}

// --- Mapping ------------------------------------------------------------------------------

function toRequest(values: {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  currentEmployer: string;
  currentTitle: string;
  totalExperienceYears: string;
  highestEducation: string;
  skills: string;
  certifications: string;
  expectedSalary: string;
  noticePeriodDays: string;
  isWillingToRelocate: 'unknown' | 'yes' | 'no';
  nationalId: string;
  jobId: string;
  source: string;
  poolId: string;
  hasConsent: boolean;
}): unknown {
  const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());
  const number = (value: string): number | null | string => {
    const cleaned = value.replace(/[,\s৳]/g, '');
    if (cleaned === '') return null;
    // Left as text when it is not a number, so the shared schema says what is wrong.
    return /^\d+(\.\d+)?$/.test(cleaned) ? Number(cleaned) : cleaned;
  };
  return {
    fullName: values.fullName,
    email: orNull(values.email),
    phone: orNull(values.phone.replace(/[\s\-()]/g, '')),
    city: values.city,
    currentEmployer: values.currentEmployer,
    currentTitle: values.currentTitle,
    totalExperienceYears: number(values.totalExperienceYears),
    highestEducation: orNull(values.highestEducation),
    skills: list(values.skills),
    certifications: list(values.certifications),
    expectedSalary: number(values.expectedSalary),
    noticePeriodDays: number(values.noticePeriodDays),
    isWillingToRelocate: values.isWillingToRelocate === 'unknown' ? null : values.isWillingToRelocate === 'yes',
    nationalId: orNull(values.nationalId.replace(/\s/g, '')),
    jobId: orNull(values.jobId),
    source: values.source,
    poolId: orNull(values.poolId),
    hasConsent: values.hasConsent,
  };
}

function list(text: string): string[] {
  const seen = new Set<string>();
  return text
    .split(/[,\n;]/)
    .map((item) => item.replace(/\s+/g, ' ').trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (item === '' || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function outcomeToast(outcome: 'created' | 'linked' | 'flagged', name: string): { message: string; detail?: string } {
  if (outcome === 'linked') return { message: `${name} was already in the system`, detail: 'The new details joined their existing record.' };
  if (outcome === 'flagged') return { message: `${name} added`, detail: 'They may already be in the system under another record — review the pair in possible duplicates.' };
  return { message: `${name} added` };
}

async function uploadCv(candidateId: string, applicationId: string | null, file: File): Promise<void> {
  const body = new FormData();
  body.append('cv', file);
  await request(`/candidates/${candidateId}/cv`, { method: 'POST', body, ...(applicationId ? { query: { applicationId } } : {}) });
}
